import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { CATEGORY_ICON, CATEGORY_LABEL } from '../lib/constants.js';

// ─── ComplianceCreateModal ────────────────────────────────────────────────────
export function ComplianceCreateModal({ reminder, locations, users, currentUser, onClose, onSave }) {
  const isEdit = !!reminder;
  const [form, setForm] = React.useState({
    title: reminder?.title || '',
    description: reminder?.description || '',
    guide_text: reminder?.guide_text || '',
    due_date: reminder?.due_date || '',
    recurrence: reminder?.recurrence || 'annual',
    category: reminder?.category || 'other',
    location_id: reminder?.location_id || '',
    shared_with: reminder?.shared_with || 'all_managers',
    notify_days_before: reminder?.notify_days_before ?? 30,
    lease_details: (() => {
      try { return reminder?.lease_details ? JSON.parse(reminder.lease_details) : null; } catch { return null; }
    })(),
  });
  const [saving, setSaving] = React.useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setLease(k, v) { setForm(f => ({ ...f, lease_details: { ...(f.lease_details || {}), [k]: v } })); }

  const isLease = form.category === 'lease';

  React.useEffect(() => {
    if (isLease && !form.lease_details) {
      setForm(f => ({ ...f, lease_details: { monthly_rent: '', annual_increase: 3, lease_start: '', lease_end: '', landlord_name: '', landlord_contact: '', notice_period: 90 } }));
    }
  }, [isLease]);

  async function handleSave() {
    if (!form.title.trim() || !form.due_date) return;
    setSaving(true);
    try {
      const payload = { ...form, lease_details: isLease ? form.lease_details : null, created_by: currentUser.id };
      if (isEdit) await api('PUT', `/api/compliance/reminders/${reminder.id}`, payload);
      else await api('POST', '/api/compliance/reminders', payload);
      onSave();
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-ink focus:outline-none focus:ring-2 focus:ring-crimson/30';
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-cream w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col fade-in shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-crimson rounded-t-2xl p-5 flex items-center justify-between flex-shrink-0">
          <h3 className="text-white font-bold text-lg">{isEdit ? 'Edit Reminder' : 'New Compliance Reminder'}</h3>
          <button onClick={onClose} className="text-white/60 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          <div>
            <label className={labelCls}>Title</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} className={inputCls} placeholder="e.g. Annual Liquor License Renewal" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} className={inputCls}>
                {['license','tax','insurance','permit','lease','other'].map(c => (
                  <option key={c} value={c}>{CATEGORY_ICON[c]} {CATEGORY_LABEL[c]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Recurrence</label>
              <select value={form.recurrence} onChange={e => set('recurrence', e.target.value)} className={inputCls}>
                <option value="annual">Annual</option>
                <option value="quarterly">Quarterly</option>
                <option value="monthly">Monthly</option>
                <option value="one-time">One-time</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Notify Days Before</label>
              <input type="number" value={form.notify_days_before} onChange={e => set('notify_days_before', parseInt(e.target.value) || 30)} className={inputCls} min="1" max="365" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Location (optional)</label>
              <select value={form.location_id} onChange={e => set('location_id', e.target.value)} className={inputCls}>
                <option value="">All Locations</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Share With</label>
              <select value={form.shared_with} onChange={e => set('shared_with', e.target.value)} className={inputCls}>
                <option value="all_managers">All Managers</option>
                {users.filter(u => ['manager','owner'].includes(u.role?.toLowerCase())).map(u => (
                  <option key={u.id} value={JSON.stringify([u.id])}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} className={inputCls} rows={2} />
          </div>
          <div>
            <label className={labelCls}>Instructions / Guide</label>
            <textarea value={form.guide_text} onChange={e => set('guide_text', e.target.value)} className={inputCls} rows={4} placeholder="Step-by-step instructions, contacts, deadlines..." />
          </div>

          {isLease && form.lease_details && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">🏢 Lease Details</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Monthly Rent ($)</label>
                  <input type="number" value={form.lease_details.monthly_rent || ''} onChange={e => setLease('monthly_rent', parseFloat(e.target.value) || '')} className={inputCls} placeholder="4500" />
                </div>
                <div>
                  <label className={labelCls}>Annual Increase (%)</label>
                  <input type="number" step="0.1" value={form.lease_details.annual_increase ?? 3} onChange={e => setLease('annual_increase', parseFloat(e.target.value) || 0)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Lease Start</label>
                  <input type="date" value={form.lease_details.lease_start || ''} onChange={e => setLease('lease_start', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Lease End</label>
                  <input type="date" value={form.lease_details.lease_end || ''} onChange={e => setLease('lease_end', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Landlord Name</label>
                  <input value={form.lease_details.landlord_name || ''} onChange={e => setLease('landlord_name', e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Landlord Contact</label>
                  <input value={form.lease_details.landlord_contact || ''} onChange={e => setLease('landlord_contact', e.target.value)} className={inputCls} placeholder="Phone or email" />
                </div>
                <div>
                  <label className={labelCls}>Notice Period (days)</label>
                  <input type="number" value={form.lease_details.notice_period ?? 90} onChange={e => setLease('notice_period', parseInt(e.target.value) || 90)} className={inputCls} />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-5 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.due_date}
            className="flex-1 py-2.5 bg-crimson text-white rounded-xl text-sm font-semibold hover:bg-crimson-dark disabled:opacity-50">
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Reminder')}
          </button>
        </div>
      </div>
    </div>
  );
}
